// @ts-nocheck
/**
 * 装備作成システム
 *
 * 責務:
 * - レシピに基づく装備作成
 * - 素材消費処理
 * - 作成結果の生成
 */

import {
  RECIPES,
  SET_BONUSES,
  getRecipe,
  getRecipesByType,
  getRecipesByTier,
  getRecipesBySet,
  getSetBonus,
  getRecipesUsingMaterial,
} from './data/recipes';

/**
 * 装備作成クラス
 */
class Crafter {
  /**
   * @param {Object} alchemyEngine - 親のAlchemyEngineインスタンス
   */
  constructor(alchemyEngine) {
    this.engine = alchemyEngine;

    // 作成履歴
    this.craftHistory = [];

    // 解放済みレシピ（将来的な進行制御用）
    this.unlockedRecipes = new Set(Object.keys(RECIPES));
  }

  /**
   * 装備を作成
   *
   * @param {string} recipeId - レシピID
   * @returns {{ success: boolean, equipment: Object|null, message: string }}
   */
  craft(recipeId) {
    const recipe = getRecipe(recipeId);
    if (!recipe) {
      return {
        success: false,
        equipment: null,
        message: `レシピが見つかりません: ${recipeId}`,
      };
    }

    // レシピが解放されているか確認
    if (!this.unlockedRecipes.has(recipeId)) {
      return {
        success: false,
        equipment: null,
        message: `レシピが解放されていません: ${recipe.name}`,
      };
    }

    // 素材チェック
    const missingMaterials = this._checkMaterials(recipe.materials);
    if (missingMaterials.length > 0) {
      return {
        success: false,
        equipment: null,
        missingMaterials,
        message: `素材が不足しています: ${missingMaterials.map(m => m.display).join(', ')}`,
      };
    }

    // 素材消費
    for (const material of recipe.materials) {
      this.engine.consumeMaterial(material.type, material.count);
    }

    // 装備生成
    const equipment = this._createEquipment(recipe);

    // 作成履歴に記録
    this._recordCraft(recipe, equipment);

    return {
      success: true,
      equipment,
      message: `${equipment.name}を作成しました！`,
    };
  }

  /**
   * 素材チェック
   *
   * @param {Object[]} requiredMaterials - 必要素材リスト
   * @returns {Object[]} 不足素材リスト
   * @private
   */
  _checkMaterials(requiredMaterials) {
    const missing = [];
    for (const material of requiredMaterials) {
      const have = this.engine.getMaterialCount(material.type);
      if (have < material.count) {
        missing.push({
          type: material.type,
          need: material.count,
          have,
          shortage: material.count - have,
          display: `${material.type}(${have}/${material.count})`,
        });
      }
    }
    return missing;
  }

  /**
   * 装備オブジェクトを生成
   *
   * @param {Object} recipe - レシピ
   * @returns {Object} 装備オブジェクト
   * @private
   */
  _createEquipment(recipe) {
    // ユニークIDを生成
    const uniqueId = `eq_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      id: uniqueId,
      recipeId: recipe.id,
      name: recipe.name,
      description: recipe.description || '',
      type: recipe.type,         // 'weapon' | 'armor' | 'accessory' | 'special'
      tier: recipe.tier,         // 'common' | 'rare' | 'epic' | 'legendary'
      level: 1,                  // 初期レベル
      awakening: 0,              // 初期覚醒
      stats: { ...recipe.baseStats },
      baseStats: { ...recipe.baseStats }, // 強化計算用に基礎値を保持
      effects: [...(recipe.effects || [])],
      setId: recipe.setId || null,
      equipped: null,            // 装備先モンスターID（null=未装備）
      locked: false,             // ロック状態
      createdAt: Date.now(),
    };
  }

  /**
   * 作成履歴を記録
   * @private
   */
  _recordCraft(recipe, equipment) {
    this.craftHistory.push({
      recipeId: recipe.id,
      equipmentId: equipment.id,
      equipmentName: equipment.name,
      tier: equipment.tier,
      timestamp: Date.now(),
    });

    // 履歴は直近100件のみ保持
    if (this.craftHistory.length > 100) {
      this.craftHistory.shift();
    }
  }

  /**
   * 作成可能なレシピ一覧を取得
   *
   * @param {Object} options - フィルタオプション
   * @returns {Object[]} レシピ一覧（作成可否情報付き）
   */
  getAvailableRecipes(options = {}) {
    const { type, tier, setId } = options;

    let recipes = Object.values(RECIPES);

    // フィルタリング
    if (type) {
      recipes = recipes.filter(r => r.type === type);
    }
    if (tier) {
      recipes = recipes.filter(r => r.tier === tier);
    }
    if (setId) {
      recipes = recipes.filter(r => r.setId === setId);
    }

    // 作成可否情報を追加
    return recipes.map(recipe => {
      const missingMaterials = this._checkMaterials(recipe.materials);
      const isUnlocked = this.unlockedRecipes.has(recipe.id);

      return {
        ...recipe,
        isUnlocked,
        canCraft: isUnlocked && missingMaterials.length === 0,
        missingMaterials,
        materialsStatus: recipe.materials.map(m => ({
          ...m,
          have: this.engine.getMaterialCount(m.type),
          isSufficient: this.engine.getMaterialCount(m.type) >= m.count,
        })),
      };
    });
  }

  /**
   * 特定の素材を使用するレシピを取得
   *
   * @param {string} materialType - 素材タイプ
   * @returns {Object[]} レシピ一覧
   */
  getRecipesForMaterial(materialType) {
    return getRecipesUsingMaterial(materialType).map(recipe => ({
      ...recipe,
      materialUsage: recipe.materials.find(m => m.type === materialType),
    }));
  }

  /**
   * セット効果を計算
   *
   * @param {Object[]} equipments - 装備リスト
   * @returns {Object[]} 発動中のセット効果
   */
  calculateSetBonuses(equipments) {
    const activeSetBonuses = [];

    // セットIDごとに装備数をカウント
    const setCounts = {};
    for (const eq of equipments) {
      if (eq.setId) {
        setCounts[eq.setId] = (setCounts[eq.setId] || 0) + 1;
      }
    }

    // 各セットの発動効果を確認
    for (const [setId, count] of Object.entries(setCounts)) {
      const setBonus = getSetBonus(setId);
      if (!setBonus) continue;

      const activeBonuses = [];
      for (const [requiredCount, bonus] of Object.entries(setBonus.bonuses)) {
        if (count >= parseInt(requiredCount)) {
          activeBonuses.push({
            requiredCount: parseInt(requiredCount),
            ...bonus,
          });
        }
      }

      if (activeBonuses.length > 0) {
        activeSetBonuses.push({
          setId,
          setName: setBonus.name,
          description: setBonus.description,
          equippedCount: count,
          activeBonuses,
        });
      }
    }

    return activeSetBonuses;
  }

  /**
   * 作成プレビュー
   *
   * @param {string} recipeId - レシピID
   * @returns {Object|null} プレビュー情報
   */
  previewCraft(recipeId) {
    const recipe = getRecipe(recipeId);
    if (!recipe) return null;

    const missingMaterials = this._checkMaterials(recipe.materials);
    const isUnlocked = this.unlockedRecipes.has(recipeId);

    return {
      recipe,
      isUnlocked,
      canCraft: isUnlocked && missingMaterials.length === 0,
      missingMaterials,
      materialsStatus: recipe.materials.map(m => ({
        ...m,
        have: this.engine.getMaterialCount(m.type),
        isSufficient: this.engine.getMaterialCount(m.type) >= m.count,
      })),
      previewEquipment: {
        name: recipe.name,
        type: recipe.type,
        tier: recipe.tier,
        stats: recipe.baseStats,
        effects: recipe.effects || [],
        setId: recipe.setId,
      },
      setBonus: recipe.setId ? getSetBonus(recipe.setId) : null,
    };
  }

  /**
   * レシピを解放
   *
   * @param {string} recipeId - レシピID
   */
  unlockRecipe(recipeId) {
    if (getRecipe(recipeId)) {
      this.unlockedRecipes.add(recipeId);
    }
  }

  /**
   * レシピをロック（主にテスト用）
   *
   * @param {string} recipeId - レシピID
   */
  lockRecipe(recipeId) {
    this.unlockedRecipes.delete(recipeId);
  }

  /**
   * 作成履歴を取得
   *
   * @returns {Object[]} 作成履歴
   */
  getCraftHistory() {
    return [...this.craftHistory];
  }

  /**
   * タイプ別のレシピ数を取得
   *
   * @returns {Object} タイプ別カウント
   */
  getRecipeCountsByType() {
    const counts = {
      weapon: 0,
      armor: 0,
      accessory: 0,
      special: 0,
    };

    for (const recipe of Object.values(RECIPES)) {
      if (counts[recipe.type] !== undefined) {
        counts[recipe.type]++;
      }
    }

    return counts;
  }

  /**
   * ティア別のレシピ数を取得
   *
   * @returns {Object} ティア別カウント
   */
  getRecipeCountsByTier() {
    const counts = {
      common: 0,
      rare: 0,
      epic: 0,
      legendary: 0,
    };

    for (const recipe of Object.values(RECIPES)) {
      if (counts[recipe.tier] !== undefined) {
        counts[recipe.tier]++;
      }
    }

    return counts;
  }

  /**
   * 状態のシリアライズ
   *
   * @returns {Object} シリアライズされた状態
   */
  serialize() {
    return {
      unlockedRecipes: Array.from(this.unlockedRecipes),
      craftHistory: this.craftHistory,
    };
  }

  /**
   * 状態のデシリアライズ
   *
   * @param {Object} data - シリアライズされた状態
   */
  deserialize(data) {
    if (data.unlockedRecipes) {
      this.unlockedRecipes = new Set(data.unlockedRecipes);
    }
    if (data.craftHistory) {
      this.craftHistory = data.craftHistory;
    }
  }
}

export default Crafter;
